import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, Loader2 } from "lucide-react";

export default function Blog() {
    const [form, setForm] = useState({ topic: "", audience: "professionals", angle: "" });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState("");

    const generate = async (e) => {
        e.preventDefault();
        if (!form.topic.trim()) return;
        setErr(""); setLoading(true); setResult(null);
        try {
            const { data } = await api.post("/blog", form);
            setResult(data);
            toast.success("Blog post generated");
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setLoading(false);
        }
    };

    const download = () => {
        if (!result) return;
        const blob = new Blob([result.full_post], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `hilakshmi_blog_${form.topic.slice(0, 30).replace(/\s+/g, "_")}.md`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Downloaded as Markdown");
    };

    const inputCls = "w-full lk-border lk-surface-2 rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--lk-primary)]";

    return (
        <div className="min-h-screen">
            <Navbar />
            <div className="max-w-4xl mx-auto px-6 py-10">
                <div className="lk-label">Blog Generator</div>
                <h1 className="font-heading text-4xl sm:text-5xl mt-2 tracking-tight">Write a full blog post</h1>
                <p className="text-[var(--lk-muted)] mt-3 max-w-xl">Lakshmi generates a structured 800-1200 word blog post using your voice profile. Download as Markdown.</p>

                <form onSubmit={generate} className="mt-10 lk-border lk-surface rounded-xl p-6 space-y-5">
                    <div>
                        <label className="lk-label block mb-2">Topic</label>
                        <input value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })}
                            className={inputCls} placeholder="e.g. Why most AI projects fail in enterprises"
                            data-testid="blog-topic-input" required />
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        <div>
                            <label className="lk-label block mb-2">Target audience</label>
                            <input value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })}
                                className={inputCls} placeholder="e.g. CTOs, tech leads" data-testid="blog-audience-input" />
                        </div>
                        <div>
                            <label className="lk-label block mb-2">Angle (optional)</label>
                            <input value={form.angle} onChange={e => setForm({ ...form, angle: e.target.value })}
                                className={inputCls} placeholder="e.g. Contrarian take, case study, how-to" data-testid="blog-angle-input" />
                        </div>
                    </div>
                    {err && <div className="text-sm text-red-400">{err}</div>}
                    <div className="flex justify-end">
                        <button type="submit" disabled={loading || !form.topic.trim()}
                            className="lk-btn-primary rounded-full px-8 py-3 flex items-center gap-2 disabled:opacity-50"
                            data-testid="blog-generate-btn">
                            {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Writing…</> : <><FileText className="w-4 h-4" />Generate Blog Post</>}
                        </button>
                    </div>
                </form>

                {result && (
                    <div className="mt-10">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="lk-label">Generated Blog Post</div>
                                <div className="text-xs text-[var(--lk-muted)] mt-1">
                                    {result.sections_count} sections · {result.total_tokens} tokens · {Math.round(result.total_latency_ms / 1000)}s
                                </div>
                            </div>
                            <button onClick={download}
                                className="lk-border rounded-full px-4 py-2 text-sm flex items-center gap-2 hover:bg-[var(--lk-surface-2)]"
                                data-testid="blog-download-btn">
                                <Download className="w-4 h-4" /> Download .md
                            </button>
                        </div>
                        <div className="lk-border lk-surface rounded-xl p-6 md:p-8 whitespace-pre-wrap font-body leading-relaxed text-sm">
                            {result.full_post}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
